// src/features/mocktests/components/WritingPage.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Paper,
  Backdrop,
  Card,
  Fade,
} from "@mui/material";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import {
  useLazyGetPracticeTestDataQuery,
  useLazyGetTestAttemptResultsQuery,
  useUpdateTestAttemptMutation,
} from "../mockTestSlice";
import { useLazyGetBundleTestDataQuery } from "../../bundles/bundleSlice";
import WritingSection from "./WritingSection";
import TestResultsPopup from "./TestResultsPopup";
import { getStoredTimerData, storeTimerData } from "../utils/practiceTimer";
import {
  InstructionWrapper,
  useInstructions,
} from "../../../components/Instructions";
import { useTestNavigation } from "../utils/testNavigation";

export default function WritingPage({ isBundle = false }) {
  const { testId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Get navigation utilities for test type
  const { baseRoute, backRoute, getTestUrl, testType } = useTestNavigation();

  // --- Instruction system integration ---
  const {
    instructionState,
    showInstructionModal,
    hideInstructionModal,
    hideConfirmationDialog,
    hideUnattemptedDialog,
    hideTimeExpiryDialog,
  } = useInstructions();

  // Show Writing instructions at section start
  const [showInitialInstructions, setShowInitialInstructions] = useState(true);

  // Check for essential state and redirect if missing (browser refresh)
  const studentAttemptId =
    location.state?.studentAttemptId ||
    location.state?.mockTest?.studentAttemptId;

  useEffect(() => {
    if (!studentAttemptId || !location.state) {
      navigate(getTestUrl(testId), { replace: true });
    }
  }, [studentAttemptId, location.state, navigate, testId, getTestUrl]);

  // Practice mode timer inputs
  const timerMinutes = location.state?.timerMinutes;
  const testAttemptId = location.state?.mockTest?.studentAttemptId;

  // Get any stored timer for this attempt
  const storedTimer = getStoredTimerData(testAttemptId);

  // Timer state (we will start it only after user chooses submission type)
  const [practiceTimerStartTime, setPracticeTimerStartTime] = useState(null);
  const [practiceTimerEndTime, setPracticeTimerEndTime] = useState(null);
  const [adjustedEndTime, setAdjustedEndTime] = useState(null); // For handling preparation time skips
  const [hasChosenSubmission, setHasChosenSubmission] = useState(false);

  // Restore in-progress timer if present (e.g., refresh after selection)
  useEffect(() => {
    if (storedTimer?.startTime && storedTimer?.endTime) {
      setPracticeTimerStartTime(storedTimer.startTime);
      setPracticeTimerEndTime(storedTimer.endTime);
      setHasChosenSubmission(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update API mutation
  const [updateTestAttempt] = useUpdateTestAttemptMutation();

  // Use bundle API when isBundle is true, otherwise use mock test API
  const [
    triggerGetMockPracticeData,
    {
      data: mockPracticeTestData,
      isLoading: mockPracticeLoading,
      error: mockPracticeError,
    },
  ] = useLazyGetPracticeTestDataQuery();
  const [
    triggerGetBundleData,
    {
      data: bundlePracticeTestData,
      isLoading: bundlePracticeLoading,
      error: bundlePracticeError,
    },
  ] = useLazyGetBundleTestDataQuery();
  
  // Use the appropriate data based on isBundle
  const triggerGetPracticeData = isBundle ? triggerGetBundleData : triggerGetMockPracticeData;
  const practiceTestData = isBundle ? bundlePracticeTestData : mockPracticeTestData;
  const practiceLoading = isBundle ? bundlePracticeLoading : mockPracticeLoading;
  const practiceError = isBundle ? bundlePracticeError : mockPracticeError;

  const [localWritingData, setLocalWritingData] = useState(null);

  // Extract timer data from API response (moved up before useEffect)
  const apiStartTime =
    practiceTestData?.data?.startTime || // From practice test data API response
    localWritingData?.data?.startTime || // From stored local data
    location.state?.mockTest?.startTime ||
    location.state?.mockTest?.testStartTime;
  const apiEndTime =
    practiceTestData?.data?.endTime || // From practice test data API response
    localWritingData?.data?.endTime || // From stored local data
    location.state?.mockTest?.endTime ||
    location.state?.mockTest?.testEndDate;

  // Extract submission status from API response
  const isWritingSubmitted =
    practiceTestData?.data?.isWritingSubmitted ||
    localWritingData?.data?.isWritingSubmitted ||
    false;

  // Once the user chooses Computer/Paper, start a fresh timer based on API duration (or fallback)
  useEffect(() => {
    if (!hasChosenSubmission) return;
    if (practiceTimerStartTime && practiceTimerEndTime) return; // already restored

    // Compute duration: prefer API start/end → else timerMinutes → else 45m
    let durationMs = 0;
    if (apiStartTime && apiEndTime) {
      durationMs =
        new Date(apiEndTime).getTime() - new Date(apiStartTime).getTime();
    } else if (timerMinutes) {
      durationMs = timerMinutes * 60 * 1000;
    } else {
      durationMs = 45 * 60 * 1000;
    }

    const now = Date.now();
    const end = now + durationMs;

    const startISO = new Date(now).toISOString();
    const endISO = new Date(end).toISOString();

    setPracticeTimerStartTime(startISO);
    setPracticeTimerEndTime(endISO);
    storeTimerData(startISO, endISO, testAttemptId);

    // ✅ Persist the timer window to backend
    (async () => {
      try {
        await updateTestAttempt({
          studentAttemptId: testAttemptId,
          testStartTime: startISO,
          testEndDate: endISO,
        }).unwrap();
      } catch (e) {
        console.error("Failed to update attempt timings", e);
      }
    })();
  }, [
    hasChosenSubmission,
    practiceTimerStartTime,
    practiceTimerEndTime,
    apiStartTime,
    apiEndTime,
    timerMinutes,
    testAttemptId,
    updateTestAttempt,
  ]);

  // Dialog and user flow states
  const [evaluationDialogOpen, setEvaluationDialogOpen] = useState(false);
  const [showEvalConfirmation, setShowEvalConfirmation] = useState(false);
  const [showSubmissionTypeDialog, setShowSubmissionTypeDialog] =
    useState(true);

  const [evaluationType] = useState("ai"); // lock to ai flow for AI-based evaluation by default
  const [selectedEvalType, setSelectedEvalType] = useState(null);
  const [submissionType, setSubmissionType] = useState("computer"); // "computer" | "paper"

  // State for WritingSection
  const [answer, setAnswer] = useState("");

  // Footer confirm dialog
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Results
  const [testResults, setTestResults] = useState(null);
  const [showResultsPopup, setShowResultsPopup] = useState(false);

  // Timer tick state
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [timerEnded, setTimerEnded] = useState(false);

  // Header visibility state
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);

  // Ref to access WritingSection methods
  const writingSectionRef = useRef();

  // API hooks
  const [getTestAttemptResults] = useLazyGetTestAttemptResultsQuery();

  // Helper: show results (practice mode)
  const showPracticeResults = async (studentAttemptIdParam) => {
    try {
      const resultsResponse = await getTestAttemptResults(
        studentAttemptIdParam
      ).unwrap();
      setTestResults(resultsResponse);
      setShowResultsPopup(true);
    } catch (resultsError) {
      alert("Error fetching results. Please try again.");
    }
  };

  // Should we show results popup after writing?
  const shouldShowResultsPopup = () => {
    const isPracticeMode = location.state?.isPracticeMode;
    const selectedSections = location.state?.selectedSections;

    if (!isPracticeMode) return true;

    if (!selectedSections?.writing) return false;

    const hasListening = selectedSections.listening;
    const hasReading = selectedSections.reading;
    return hasListening || hasReading;
  };

  // Show Writing instructions when data is loaded
  useEffect(() => {
    if (showInitialInstructions && localWritingData) {
      const mode = location.state?.isPracticeMode ? "practice" : "simulation";
      showInstructionModal("writing", null, mode);
      setShowInitialInstructions(false);
    }
  }, [
    showInitialInstructions,
    localWritingData,
    showInstructionModal,
    location.state,
  ]);

  // Debug location state
  useEffect(() => {
    // console.log removed
  }, [location.state]);

  useEffect(() => {
    const practiceDataFromState = location.state?.practiceData;
    if (practiceDataFromState) {
      setLocalWritingData(practiceDataFromState);
      return;
    }
    setLocalWritingData(null);

    const studentAttemptIdLocal = location.state?.studentAttemptId;
    if (studentAttemptIdLocal) {
      const apiParams = {
        studentAttemptId: studentAttemptIdLocal,
        currentSection: "writing",
        writingId: location.state?.writingTestId,
      };
      triggerGetPracticeData(apiParams);
    }
    // eslint-disable-next-line
  }, [
    location.state,
    location.state?.studentAttemptId,
    location.state?.practiceData,
    location.state?.writingTestId,
    triggerGetPracticeData,
  ]);

  useEffect(() => {
    if (practiceTestData) setLocalWritingData(practiceTestData);
  }, [practiceTestData]);

  // Handle adjustment of end time from WritingSection (e.g., when preparation time is skipped)
  const handleEndTimeAdjustment = useCallback((newEndTime) => {
    // console.log removed
    setAdjustedEndTime(newEndTime);
  }, []);

  const handleResultsReceived = (results) => {
    if (shouldShowResultsPopup()) {
      setTestResults(results);
      setShowResultsPopup(true);
    }
  };

  const handleTimeUp = useCallback(() => {
    // console.log removed
  }, []);

  // Tick the countdown only after user has chosen and we have an endTime
  // Stop timer when results popup is shown or test is submitted
  useEffect(() => {
    if (!hasChosenSubmission || !practiceTimerEndTime) return;

    // Stop timer if results popup is showing or test is already submitted
    if (showResultsPopup || isWritingSubmitted) {
      return;
    }

    const updateTimer = () => {
      const now = Date.now();
      // Use adjusted end time if available, otherwise use original end time
      const effectiveEndTime = adjustedEndTime || practiceTimerEndTime;
      const endMs = new Date(effectiveEndTime).getTime();
      const remainingSeconds = Math.max(0, Math.floor((endMs - now) / 1000));
      setTimeRemaining(remainingSeconds);

      if (remainingSeconds <= 0 && !timerEnded) {
        setTimerEnded(true);
        handleTimeUp();
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [
    hasChosenSubmission,
    practiceTimerEndTime,
    adjustedEndTime,
    handleTimeUp,
    timerEnded,
    showResultsPopup,
    isWritingSubmitted,
  ]);

  // Transform API data for WritingSection
  const transformWritingData = (apiData) => {
    const writingTest = apiData?.data?.writingTest;
    if (!writingTest) return null;
    return {
      title: writingTest.title || "",
      subTitle: writingTest.subTitle || "",
      instructions: writingTest.instructions || "",
      writingPrompt: writingTest.writingPrompt || "",
      caseNotes: (writingTest.writingSection || []).map((section) => ({
        title: section.title,
        instructions: section.instructions,
        subSections: (section.subSections || []).map((sub) => ({
          label: sub.label,
          content: sub.content,
          visible: sub.visible,
          _id: sub._id,
        })),
        _id: section._id,
      })),
    };
  };
  const dataToTransform = localWritingData || location.state?.practiceData;
  const writingData = transformWritingData(dataToTransform);

  const handleSubmissionTypeSelect = (type) => {
    setSubmissionType(type);
    setEvaluationDialogOpen(false);
    setShowSubmissionTypeDialog(false);
    setHasChosenSubmission(true); // <-- start the timer flow
  };

  // Footer: confirm submit
  const handleConfirmSubmit = async () => {
    setConfirmOpen(false);
    if (writingSectionRef.current?.submitAnswer) {
      await writingSectionRef.current.submitAnswer();

      // In simulation mode, fetch and show results for listening and reading
      if (!location.state?.isPracticeMode && studentAttemptId) {
        try {
          const resultsResponse = await getTestAttemptResults(
            studentAttemptId
          ).unwrap();
          setTestResults(resultsResponse);
          setShowResultsPopup(true);
        } catch (resultsError) {
          console.error("Error fetching results:", resultsError);
        }
      }
    }
  };

  // Time formatting helpers
  const pad2 = (n) => String(n).padStart(2, "0");
  const fmtClock = (s) => {
    if (s == null) return "--:--:--";
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = Math.floor(s % 60);
    return `${pad2(h)}:${pad2(m)}:${pad2(sec)}`;
  };

  const getDisplayTime = () => {
    if (!hasChosenSubmission || !practiceTimerEndTime) return fmtClock(null);
    return fmtClock(timeRemaining);
  };

  if (practiceLoading || (!localWritingData && !location.state?.practiceData)) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress size={60} thickness={5} />
        <Typography sx={{ ml: 2 }}>Loading writing data...</Typography>
      </Box>
    );
  }

  if (practiceError) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <Typography color="error" variant="h6" gutterBottom>
          Error loading writing test:{" "}
          {practiceError.message || JSON.stringify(practiceError)}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Instruction System Wrapper */}
      <InstructionWrapper
        instructionState={instructionState}
        onInstructionNext={hideInstructionModal}
        onInstructionClose={hideInstructionModal}
        onConfirmationConfirm={() => {
          hideConfirmationDialog();
          hideUnattemptedDialog();
        }}
        onConfirmationClose={hideConfirmationDialog}
        onUnattemptedContinue={() => {
          hideUnattemptedDialog();
        }}
        onUnattemptedClose={() => {
          hideUnattemptedDialog();
        }}
        onTimeExpiryContinue={hideTimeExpiryDialog}
        timeRemaining={null}
        unattemptedCount={0}
        nextSection="speaking"
      />

      <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
        {/* HEADER TOGGLE BUTTON - Fixed position when header is hidden */}
        {!isHeaderVisible && (
          <Fade in={!isHeaderVisible} timeout={300}>
            <Box
              sx={{
                position: "fixed",
                top: 10,
                left: 0,
                right: 0,
                zIndex: 30,
                display: "flex",
                justifyContent: "center",
                pt: 1,
                pb: 1,
                bgcolor: "rgba(247, 247, 247, 0.95)",
                backdropFilter: "blur(10px)",
                borderBottom: "1px solid rgba(157, 34, 53, 0.1)",
              }}
            >
              <Button
                onClick={() => setIsHeaderVisible(true)}
                onTouchStart={(e) => {
                  e.stopPropagation();
                  console.log("Show Header button touched");
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  console.log("Show Header button touch ended");
                }}
                variant="contained"
                size="small"
                sx={{
                  background:
                    "linear-gradient(135deg, #9D2235 0%, #7a182a 100%)",
                  color: "white",
                  px: 2.5,
                  py: 0.75,
                  fontSize: "0.8rem",
                  fontWeight: 500,
                  boxShadow: "0px 4px 16px rgba(157, 34, 53, 0.3)",
                  border: "none",
                  borderRadius: 25,
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #7a182a 0%, #5d1220 100%)",
                    transform: "translateY(-1px)",
                    boxShadow: "0px 6px 20px rgba(157, 34, 53, 0.4)",
                  },
                  "&:active": {
                    transform: "translateY(0px)",
                    background:
                      "linear-gradient(135deg, #5d1220 0%, #4a0e1a 100%)",
                  },
                  textTransform: "none",
                  // Enhanced mobile support
                  minHeight: { xs: 44, sm: "auto" },
                  minWidth: { xs: 120, sm: "auto" },
                  touchAction: "manipulation",
                  WebkitTapHighlightColor: "transparent",
                  userSelect: "none",
                }}
              >
                ▼ Show Header
              </Button>
            </Box>
          </Fade>
        )}

        {/* CONTENT AREA */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            overflow: "hidden",
            flexDirection: "column",
            pt: !isHeaderVisible ? { xs: "70px", sm: "75px" } : 0,
            transition: "padding-top 0.3s ease-in-out",
          }}
        >
          {/* Only show WritingSection when all modals are closed */}
          {!evaluationDialogOpen &&
            !showEvalConfirmation &&
            !showSubmissionTypeDialog && (
              <WritingSection
                ref={writingSectionRef}
                writingData={writingData}
                answer={answer}
                onAnswerChange={setAnswer}
                onTimeUp={handleTimeUp}
                timer="no-limit"
                mode={
                  location.state?.isPracticeMode ? "practice" : "simulation"
                }
                submissionType={submissionType}
                studentAttemptId={location.state?.studentAttemptId || ""}
                writingTestId={
                  location.state?.writingId ||
                  location.state?.writingTestId ||
                  ""
                }
                isAiBased={evaluationType === "ai"}
                isSubmitted={isWritingSubmitted}
                onResultsReceived={
                  shouldShowResultsPopup() ? handleResultsReceived : null
                }
                shouldFetchResults={shouldShowResultsPopup()}
                // Use our post-selection timer
                // Use API timer data if available, otherwise use our post-selection timer
                startTime={apiStartTime || practiceTimerStartTime}
                endTime={apiEndTime || adjustedEndTime || practiceTimerEndTime}
                onEndTimeAdjustment={handleEndTimeAdjustment} // ✅ pass the callback for time adjustments
                updateTestAttempt={updateTestAttempt} // ✅ pass the mutation function
                testId={testId}
                isHeaderVisible={isHeaderVisible} // ✅ pass header visibility state
                instrOpen={
                  instructionState.showInstructions &&
                  instructionState.currentSection === "writing"
                }
                // Footer-related props
                confirmOpen={confirmOpen}
                setConfirmOpen={setConfirmOpen}
                onConfirmSubmit={handleConfirmSubmit}
                onBack={() => {
                  const sectionOrder = location.state?.sectionOrder || [];
                  const currentSectionIndex =
                    location.state?.currentSectionIndex || 0;

                  if (currentSectionIndex > 0) {
                    const previousSection =
                      sectionOrder[currentSectionIndex - 1];
                    navigate(`${baseRoute}/${testId}/${previousSection}`, {
                      state: {
                        ...location.state,
                        currentSectionIndex: currentSectionIndex - 1,
                      },
                    });
                  }
                }}
                isLastQuestion={
                  location.state?.isPracticeMode &&
                  location.state?.sectionOrder &&
                  location.state?.currentSectionIndex ===
                    location.state?.sectionOrder.length - 1
                }
                onFinish={async () => {
                  if (location.state?.isPracticeMode) {
                    const sid = location.state?.studentAttemptId;
                    try {
                      await showPracticeResults(sid);
                    } catch (error) {
                      navigate("/mock-tests");
                    }
                  }
                }}
              />
            )}
        </Box>

        {/* Footer */}
        {!evaluationDialogOpen &&
          !showEvalConfirmation &&
          !showSubmissionTypeDialog && (
            <Box
              sx={{
                bgcolor: "#fff",
                borderTop: "1px solid #e0e0e0",
                px: { xs: 1, sm: 4 },
                py: { xs: 1.5, sm: 2 },
                position: "sticky",
                bottom: 0,
                zIndex: 10,
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {location.state?.sectionOrder &&
                location.state?.currentSectionIndex > 0 && (
                  <Button
                    variant="outlined"
                    onClick={() => {
                      const sectionOrder = location.state?.sectionOrder || [];
                      const currentSectionIndex =
                        location.state?.currentSectionIndex || 0;

                      if (currentSectionIndex > 0) {
                        const previousSection =
                          sectionOrder[currentSectionIndex - 1];
                        navigate(`${baseRoute}/${testId}/${previousSection}`, {
                          state: {
                            ...location.state,
                            currentSectionIndex: currentSectionIndex - 1,
                          },
                        });
                      }
                    }}
                    onTouchStart={(e) => {
                      e.stopPropagation();
                      console.log("Back button touched");
                    }}
                    onTouchEnd={(e) => {
                      e.stopPropagation();
                      console.log("Back button touch ended");
                    }}
                    sx={{
                      minWidth: { xs: 80, sm: 100 },
                      textTransform: "none",
                      borderColor: "#9D2235",
                      color: "#9D2235",
                      py: { xs: 0.75, sm: 1 },
                      px: { xs: 1.5, sm: 2 },
                      borderRadius: 3,
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                      "&:hover": {
                        borderColor: "#9D2235",
                        backgroundColor: "rgba(157, 34, 53, 0.04)",
                      },
                      "&:active": {
                        borderColor: "#7a182a",
                        backgroundColor: "rgba(157, 34, 53, 0.08)",
                        transform: "scale(0.98)",
                      },
                      // Enhanced mobile support
                      minHeight: { xs: 44, sm: "auto" },
                      touchAction: "manipulation",
                      WebkitTapHighlightColor: "transparent",
                      userSelect: "none",
                    }}
                  >
                    Back
                  </Button>
                )}
              {!(
                location.state?.sectionOrder &&
                location.state?.currentSectionIndex > 0
              ) && <Box />}
              <Button
                variant="contained"
                size="large"
                onClick={() => setConfirmOpen(true)}
                onTouchStart={(e) => {
                  e.stopPropagation();
                  console.log("Submit/Finish button touched");
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  console.log("Submit/Finish button touch ended");
                }}
                sx={{
                  minWidth: { xs: 120, sm: 140 },
                  textTransform: "none",
                  bgcolor: "#9D2235",
                  py: { xs: 0.75, sm: 1 },
                  px: { xs: 2, sm: 3 },
                  borderRadius: 3,
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                  "&:hover": {
                    bgcolor: "#7a182a",
                  },
                  "&:active": {
                    bgcolor: "#5d1220",
                    transform: "scale(0.98)",
                  },
                  // Enhanced mobile support
                  minHeight: { xs: 44, sm: "auto" },
                  touchAction: "manipulation",
                  WebkitTapHighlightColor: "transparent",
                  userSelect: "none",
                  boxShadow: "0 2px 8px rgba(157, 34, 53, 0.2)",
                  transition: "all 0.2s ease-in-out",
                }}
              >
                {location.state?.isPracticeMode &&
                location.state?.sectionOrder &&
                location.state?.currentSectionIndex ===
                  location.state?.sectionOrder.length - 1
                  ? "Finish"
                  : "Submit"}
              </Button>
            </Box>
          )}
      </Box>

      {/* Backdrop for modal blur */}
      <Backdrop
        open={
          evaluationDialogOpen ||
          showEvalConfirmation ||
          showSubmissionTypeDialog
        }
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backdropFilter: "blur(5px)",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        }}
      />

      {/* (Disabled) Evaluation Method Dialog - kept but never opened */}
      <Dialog
        open={
          evaluationDialogOpen &&
          !showEvalConfirmation &&
          !showSubmissionTypeDialog
        }
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 700,
            color: "#9D2235",
            fontSize: 28,
            pt: 4,
          }}
        >
          Choose Evaluation Method
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="subtitle1"
            sx={{ textAlign: "center", mb: 4, fontSize: 18, color: "#555" }}
          >
            Select how you would like your writing to be evaluated
          </Typography>
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} sm={6}>
              <Paper
                elevation={3}
                onClick={() => setSelectedEvalType("human")}
                sx={{
                  p: 4,
                  textAlign: "center",
                  borderRadius: 3,
                  cursor: "pointer",
                  transition: "all 0.3s",
                  border: "2px solid transparent",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: 6,
                    border: "2px solid #9D2235",
                  },
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  background:
                    "linear-gradient(135deg, #ffffff 0%, #f8f8f8 100%)",
                }}
              >
                <Box
                  sx={{
                    bgcolor: "#FFE0E4",
                    borderRadius: "50%",
                    p: 2,
                    mb: 3,
                    width: 100,
                    height: 100,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <PersonOutlineOutlinedIcon
                    sx={{ fontSize: 60, color: "#9D2235" }}
                  />
                </Box>
                <Typography variant="h5" fontWeight={600} sx={{ mb: 1 }}>
                  Human-Based Evaluation
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Get detailed feedback from our expert human evaluators
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      {/* (Disabled) Confirm Selection Dialog - kept but never opened */}
      <Dialog
        open={showEvalConfirmation}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 2 } }}
      >
        <DialogTitle
          sx={{ textAlign: "center", color: "#9D2235", fontWeight: 600 }}
        >
          Confirm Selection
        </DialogTitle>
        <DialogContent>
          <Typography align="center" sx={{ mb: 2 }}>
            You have selected{" "}
            <strong>
              {selectedEvalType === "ai" ? "AI-Based" : "Human-Based"}{" "}
              Evaluation
            </strong>
            .<br />
            Do you want to continue with this selection?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            onClick={() => setShowEvalConfirmation(false)}
            variant="outlined"
            sx={{
              borderColor: "#9D2235",
              color: "#9D2235",
              "&:hover": {
                borderColor: "#7a182a",
                bgcolor: "rgba(157, 34, 53, 0.05)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => setShowEvalConfirmation(false)}
            variant="contained"
            sx={{
              bgcolor: "#9D2235",
              "&:hover": { bgcolor: "#7a182a" },
              ml: 2,
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* The ONLY popup we open by default */}
      <Dialog
        open={showSubmissionTypeDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 2 } }}
      >
        <DialogTitle
          sx={{ textAlign: "center", color: "#9D2235", fontWeight: 600 }}
        >
          Choose Submission Method
        </DialogTitle>
        <DialogContent>
          <Typography align="center" sx={{ mb: 2 }}>
            How do you want to submit your writing for human evaluation?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            onClick={() => handleSubmissionTypeSelect("computer")}
            onTouchStart={(e) => {
              e.stopPropagation();
              console.log("Computer-Based button touched");
            }}
            onTouchEnd={(e) => {
              e.stopPropagation();
              console.log("Computer-Based button touch ended");
            }}
            variant="contained"
            sx={{
              bgcolor: "#9D2235",
              "&:hover": { bgcolor: "#7a182a" },
              "&:active": {
                bgcolor: "#5d1220",
                transform: "scale(0.98)",
              },
              minWidth: 120,
              // Enhanced mobile support
              minHeight: { xs: 44, sm: "auto" },
              touchAction: "manipulation",
              WebkitTapHighlightColor: "transparent",
              userSelect: "none",
              py: { xs: 1, sm: 1.5 },
              px: { xs: 2, sm: 3 },
              borderRadius: 3,
              fontSize: { xs: "0.875rem", sm: "1rem" },
              fontWeight: 600,
              transition: "all 0.2s ease-in-out",
            }}
          >
            Computer-Based
          </Button>
          <Button
            onClick={() => handleSubmissionTypeSelect("paper")}
            onTouchStart={(e) => {
              e.stopPropagation();
              console.log("Paper-Based button touched");
            }}
            onTouchEnd={(e) => {
              e.stopPropagation();
              console.log("Paper-Based button touch ended");
            }}
            variant="contained"
            sx={{
              bgcolor: "#666",
              "&:hover": { bgcolor: "#333" },
              "&:active": {
                bgcolor: "#222",
                transform: "scale(0.98)",
              },
              minWidth: 120,
              ml: 2,
              // Enhanced mobile support
              minHeight: { xs: 44, sm: "auto" },
              touchAction: "manipulation",
              WebkitTapHighlightColor: "transparent",
              userSelect: "none",
              py: { xs: 1, sm: 1.5 },
              px: { xs: 2, sm: 3 },
              borderRadius: 3,
              fontSize: { xs: "0.875rem", sm: "1rem" },
              fontWeight: 600,
              transition: "all 0.2s ease-in-out",
            }}
          >
            Paper-Based
          </Button>
        </DialogActions>
      </Dialog>

      {/* Submission Confirmation Dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 20px 40px rgba(157, 34, 53, 0.15)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#9D2235",
            color: "white",
            textAlign: "center",
            py: 3,
            fontSize: "1.5rem",
            fontWeight: 600,
            borderBottom: "none",
          }}
        >
          {location.state?.isPracticeMode &&
          location.state?.sectionOrder &&
          location.state?.currentSectionIndex ===
            location.state?.sectionOrder.length - 1
            ? "🏁 Finish Test"
            : "📤 Submit Answer"}
        </DialogTitle>
        <DialogContent
          sx={{
            backgroundColor: "white",
            py: 4,
            px: 4,
            textAlign: "center",
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="h6"
              sx={{
                color: "#9D2235",
                fontWeight: 600,
                mb: 2,
              }}
            >
              ⚠️ Confirmation Required
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#555",
                lineHeight: 1.6,
                fontSize: "1.1rem",
              }}
            >
              {location.state?.isPracticeMode &&
              location.state?.sectionOrder &&
              location.state?.currentSectionIndex ===
                location.state?.sectionOrder.length - 1
                ? "Are you sure you want to finish this test? You won't be able to make any changes after submission."
                : "Are you sure you want to submit your answer? You won't be able to make any changes after submission."}
            </Typography>
          </Box>
          <Box
            sx={{
              backgroundColor: "#f8f9fa",
              borderRadius: 2,
              p: 2,
              mt: 3,
              border: "1px solid #e9ecef",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "#666",
                fontStyle: "italic",
              }}
            >
              💡 Please review your work before proceeding
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            backgroundColor: "#f8f9fa",
            p: 3,
            gap: 2,
            justifyContent: "center",
          }}
        >
          <Button
            onClick={() => setConfirmOpen(false)}
            variant="outlined"
            sx={{
              borderColor: "#9D2235",
              color: "#9D2235",
              px: 4,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
              textTransform: "none",
              fontSize: "1rem",
              "&:hover": {
                borderColor: "#7a1a2a",
                backgroundColor: "rgba(157, 34, 53, 0.04)",
              },
            }}
          >
            ← Cancel
          </Button>
          <Button
            onClick={handleConfirmSubmit}
            disabled={location.state?.isPracticeMode && isWritingSubmitted}
            variant="contained"
            sx={{
              backgroundColor: "#9D2235",
              color: "white",
              px: 4,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
              textTransform: "none",
              fontSize: "1rem",
              boxShadow: "0 4px 12px rgba(157, 34, 53, 0.3)",
              "&:hover": {
                backgroundColor: "#7a1a2a",
                boxShadow: "0 6px 16px rgba(157, 34, 53, 0.4)",
              },
            }}
          >
            {location.state?.isPracticeMode &&
            location.state?.sectionOrder &&
            location.state?.currentSectionIndex ===
              location.state?.sectionOrder.length - 1
              ? "✓ Finish Test"
              : "✓ Submit Answer"}
          </Button>
        </DialogActions>
      </Dialog>

        {/* Test Results Popup */}
        <TestResultsPopup
          open={showResultsPopup}
          onClose={() => setShowResultsPopup(false)}
          resultsData={testResults}
          onContinue={() => navigate(backRoute)}
          attemptId={studentAttemptId}
          mockTestId={testId}
        />
    </>
  );
}
